import type { ReactNode } from 'react'
import { needsProfileSetup } from '@/utils/auth/auth-sync'
import { Navigate, useLocation } from 'react-router-dom'

import { AuthConnectionRetry } from './AuthConnectionRetry'
import { useAuth } from './AuthProvider'

interface ProtectedRouteProps {
  children: ReactNode
  redirectTo?: string
  requiredRoles?: string[]
  skipProfileCheck?: boolean
}

export function ProtectedRoute({
  children,
  redirectTo = '/login',
  requiredRoles = [],
  skipProfileCheck = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isAuthUnavailable, isLoading, user } = useAuth()
  const location = useLocation()

  // Return null during loading to prevent duplicate loading indicators
  // App.tsx now handles the central loading state
  if (isLoading) {
    return null
  }

  // The auth check never reached the API (restart, network blip). The session
  // may still be valid: stay here and reconnect rather than sending the user
  // to /login.
  if (!isAuthenticated && isAuthUnavailable) {
    return <AuthConnectionRetry />
  }

  // Not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    // Preserve the full path including org slug
    const returnPath = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${redirectTo}?redirect=${returnPath}`} replace />
  }

  // Check if the user needs to complete profile setup
  if (!skipProfileCheck && needsProfileSetup(user)) {
    // Don't redirect if we're already on the user-setup page
    if (!location.pathname.startsWith('/user-setup')) {
      return <Navigate to="/user-setup" replace />
    }
  }

  // Check for required roles
  if (requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.includes(user.role)

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />
    }
  }
  return <>{children}</>
}
